import ConfirmModal from 'components/ConfirmModal'
import { FormTip, Label, MoneyInput, RadioGroup } from 'components/form'
import Icon from 'components/Icon'
import PropTypes from 'prop-types'
import { useContext, useEffect } from 'react'
import { Alert, Nav, Tab, Tabs } from 'react-bootstrap'
import Form from 'react-bootstrap/Form'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import API from 'state/API'
import { useServerConstants } from 'state/Constants'
import { useGroupedItemsByCategory } from 'state/Items'
import { adjustOrder } from 'state/Orders'
import { getLoading } from 'state/Request'
import { useSimpleState } from 'utils'
import FMT from 'utils/fmt'
import OrderContext from '../_context'

const TYPES   = {
	CHARGE: 'charge',
	REFUND: 'refund',
	TIP   : 'tip',
}
const ORIGINS = {
	ORDER: 'order',
	MENU: 'menu',
}

export default function NewAdjustmentModal({ show, doClose }) {
	const { t }       = useTranslation()
	const dispatch    = useDispatch()
	const ORDER_CONST = useServerConstants().VALUES.ORDER

	/** @type Order */
	const order      = useContext(OrderContext)
	const allowTips  = order.useRestaurant().allow_tips
	const item       = useSimpleState(order.originalItems.length == 1? order.originalItems[0].id : '')
	const reason     = useSimpleState()
	const origin     = useSimpleState(ORIGINS.ORDER)
	const type       = useSimpleState()
	const amount     = useSimpleState(0)
	const original   = useSimpleState()

	const itemGroups   = useGroupedItemsByCategory()
	const loadingItems = useSelector(getLoading(API.RESOURCES.ITEMS))

	//region validation rules
	const invalidMsg = useSimpleState(false)
	useEffect(() => {
		if (!type.val) {
			return invalidMsg.set(t('Select the adjustment type: charge, refund or tip.'))
		}
		if (type.val != TYPES.TIP && !item.val) {
			return invalidMsg.set(t('Select an item to adjust, or a new one to add to the order.'))
		}
		if (!amount.val || amount.val <= 0) {
			return invalidMsg.set(t('Enter an amount.'))
		}
		if (type.val == TYPES.TIP) {
			if (order.tip_value !== null && amount.val < order.tip_value) {
				return invalidMsg.set(<Trans>The new tip cannot be smaller than the current one ({{ tip: FMT.money(order.tip_value) }}). If you need to issue a refund, please contact Support.</Trans>)
			}

			const maxTip = Math.floor(order.tippable_amount * ORDER_CONST.ADJ_MAX_TIP_AMOUNT)
			if (amount.val > maxTip) {
				return invalidMsg.set(<Trans>The new tip cannot be greater than {{ pct: ORDER_CONST.ADJ_MAX_TIP_AMOUNT * 100 }}% of the order total - that is, up to <b><tt>{{ maxTip: FMT.money(maxTip) }}</tt></b>.</Trans>)
			}
		} else {
			//ignoring this validation if it's a new MenuItem (for which it would be too complex to find the correct price)
			const maxCharge         = order.total_original_items * ORDER_CONST.ADJ_MAX_ITEM_CHARGE
			const totalItemsWithAdj = order.total_all_items
			const newTentativeTotal = totalItemsWithAdj + amount.val
			if (newTentativeTotal > maxCharge) {
				return invalidMsg.set(<Trans>The total of adjustments can't be greater than the original subtotal. You only can add up to <b><tt>{{ diff: FMT.money(maxCharge - totalItemsWithAdj) }}</tt></b>.</Trans>)
			}
		}
		invalidMsg.set(false)
	}, [item.val, amount.val, type.val, order.total_price]) // eslint-disable-line react-hooks/exhaustive-deps
	//endregion

	//region onChange functions
	function onTypeChange(value) {
		type.set(value)
		original.set(value == TYPES.TIP? order.tip_formatted : null)
	}

	function onOriginChange(value) {
		origin.set(value)
		item.set('')
		// reason.set('')
	}
	//endregion

	async function doSubmit() {
		const finalOrigin = (type.val == TYPES.TIP)? null : origin.val
		await dispatch(adjustOrder(order.id, type.val, item.val, amount.val, reason.val, finalOrigin))
		doResetAndClose()
	}

	function doResetAndClose() {
		[item, reason, type, amount, original].forEach(simpleState => simpleState.reset())
		doClose()
	}

	return <ConfirmModal {...{ show, doSubmit }} doClose={doResetAndClose}
	                     confirmTexts={origin.val == ORIGINS.ORDER?
		                     [t('Adjust it'), t('Adjusting')] :
		                     [t('Add it'), t('Adding')]
											 }
	                     confirmIcon={origin.val == ORIGINS.ORDER? ['far', 'money-check-edit-alt'] : 'plus-circle'}
	                     title={allowTips? t('Adjust payment/tip') : t('Adjust payment')}
	                     disabled={invalidMsg.val}
	                     size="md" id="modal-adjustments"
	>
		<Nav activeKey={type.val} onSelect={onTypeChange} id="tabs-type" variant="tabs" as="nav">
			<Nav.Item>
				<Nav.Link eventKey={TYPES.CHARGE} className="text-danger">
					<Icon i="money-bill-wave"/> {t('Charge')}
				</Nav.Link>
			</Nav.Item>
			<Nav.Item>
				<Nav.Link eventKey={TYPES.REFUND} className="text-success">
					<Icon.Slashed i="money-bill-wave"/> <span className="ml-1">{t('Refund')}</span>
				</Nav.Link>
			</Nav.Item>
			{!allowTips? null : <Nav.Item>
				<Nav.Link eventKey={TYPES.TIP} className="text-info">
					<Icon i="hand-holding-dollar"/> {t('Tip')}
				</Nav.Link>
			</Nav.Item>}
		</Nav>

		{!type.val && <Alert variant="primary" className="text-center">
			<Icon i="info-circle"/>{' '}{t('Select what you want to adjust above.')}
		</Alert>}

		{type.val == TYPES.REFUND && <Alert variant="warning" className="text-center">
			{t('This feature is not available yet. To make a refund, please reach out to Support.')}
			{/* TODO once this is allowed, we should also remove the validation rule that disables reducing the tip amount */}
		</Alert>}

		{[TYPES.CHARGE/*, TYPES.REFUND*/].includes(type.val) && <>
			<Tabs id="tabs-origin" onSelect={onOriginChange} activeKey={origin.val}>
				<Tab eventKey={ORIGINS.ORDER}
				     title={<><Icon i={['far', 'money-check-edit-alt']}/> {t('Adjust order item')}</>}>
					<Form.Group controlId="item">
						<Label required>{t('Which item?')}</Label>
						<OrderedItemsSelect item={item}/>
					</Form.Group>
				</Tab>
				<Tab eventKey={ORIGINS.MENU} title={<><Icon i="plus-circle"/> {t('New item')}</>}>
					<Form.Group controlId="item">
						<Label required>{t('Select an item from the menu:')}</Label>
						<Form.Control as="select" onChange={item.set} disabled={loadingItems} value={item.val}>
							<option value="">{loadingItems? t('Loading...') : '---'}</option>
							{itemGroups.map(({ category, items }) => (
								<optgroup label={category? t('{{x}}:', { x: category.display_name }) : t('Non-categorized items:')}>
									{items.map(item => <option key={item.id} value={item.id}>{item.display_name_without_HTML}</option>)}
								</optgroup>
							))}
						</Form.Control>
					</Form.Group>
				</Tab>
			</Tabs>

			<Form.Group controlId="reason">
				<Label>{t('Reason for changing:')}</Label>
				<Form.Control as="textarea" onChange={reason.set} value={reason.val} maxLength={100}
				              placeholder={t('e.g.: Requested by J. Doe')}/>
				<FormTip>
					{origin.val == ORIGINS.ORDER?
						t('Describe the change in the selected item.') :
						t('This will be added as a note to the new item.')}{' '}
					{t('It will be visible to the customer.')}
				</FormTip>
			</Form.Group>
		</>}

		{type.val && type.val != TYPES.REFUND && <Form.Group>
			<Label required controlId="amount">
				{type.val == TYPES.TIP? t('New tip:') : t('Amount:')}
			</Label>
			<MoneyInput id="amount" onChange={v => amount.set(FMT.demoney(v))} defaultValue={amount.val} placeholder="$X.XX"/>
			{type.val != TYPES.TIP && origin.val == ORIGINS.ORDER && <FormTip><Trans>This will be the total charge/refund for this adjustment, regardless of the item's original quantity.</Trans></FormTip> /* TODO can we show this only when the selected item has qty > 1? */}
			{original.val && <FormTip><Trans>This will <u>replace</u> the current tip, which is <b>{{value: original.val}}</b>.</Trans></FormTip>}
		</Form.Group>}
	</ConfirmModal>
}

NewAdjustmentModal.propTypes = {
	show   : PropTypes.bool.isRequired,
	doClose: PropTypes.func.isRequired,
}

function OrderedItemsSelect({ item }) {
	/** @type Order */
	const order = useContext(OrderContext)
	const { t } = useTranslation()
	const radio = (order.originalItems.length <= 3)

	const items = order.originalItems
		.map(/** @type Item */ item => {
			let name = `${item.quantity}× ${item.display_name}`
			let buddy = (item.menubuddy)? t('({{x}})', { x: t('for {{buddy}}', { buddy: item.menubuddy }) }) : ''
			return [item.id, radio? <>{name} <small>{buddy}</small></> : `${name} ${buddy}`]
		})

	if (radio) {
		return <RadioGroup name="item" values={Object.fromEntries(items)} state={item.val} onChange={item.set}/>
	} else {
		return <Form.Control as="select" onChange={item.set}>
			<option value="">---</option>
			{items.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
		</Form.Control>
	}
}
